import { PropType } from 'vue';
import debounce from 'lodash.debounce';
import { Options, Vue } from 'vue-class-component';
import {
  PlantDTO,
  PlantListDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '../../../../core/services/plants';
import {
  PlantEntityType,
  PlantFlatEntityDTO,
} from '@/../client-generator/generated-clients/configurations';
import { NAME_VALIDATION_DEBOUNCE_TIME } from '@/core/utils/utils';
import { SET_APP_LOADING } from '../../../../core/store/action-types';
import { NamingService, ValidablePlant } from '@/core/services/naming.service';
import NumberButtons from '../../../components/NumberButtons/NumberButtons.vue';

@Options({
  name: 'DialogPlantDuplicate',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    plant: {
      type: Object as PropType<PlantDTO>,
      required: true,
    },
  },
  watch: {
    isVisible: async function (newValue) {
      this.isDialogVisible = newValue;

      if (!this.plant.desktopId || !this.isDialogVisible) {
        this.dispose();
        return;
      }

      await this.doFirstPrefetch();
      await this.initializeFirstName();
    },
    plantsCount: async function (newValue, prevValue) {
      await this.suggestNameIfNeeded(prevValue, newValue);
    },
  },
  emits: ['hide-dialog', 'plant-edited', 'fetch-plants'],
  components: {
    NumberButtons,
  },
})
export default class DialogPlantCreate extends Vue {
  plantsCount = 1;
  isLoading = false;
  isVisible!: boolean;
  plant!: PlantListDTO;
  isDialogVisible = false;
  entitiesPrefetchDone = false;

  plantsToCreate: ValidablePlant[] = [];

  get dialogTitle() {
    return `${this.$t('AppResource.PlantDuplication')}: ${ this.plant.name}`;
  }

  get isConfirmDisabled(): boolean {
    const allValidNames = this.plantsToCreate.every((p) => !p.isInvalid);
    console.log(allValidNames);
    return !allValidNames;
  }

  async doConfirm() {
    this.$emit('hide-dialog');
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      await plantsClient.duplicatePlant(
        +this.plant.desktopId!,
        this.plantsToCreate.map((p) => p.name) as string[]
      );
      this.$toast.add({
        severity: 'success',
        summary: this.$t('plantDialogDuplicate.success'),
        closable: true,
        life: 3000,
      });
    } catch (e) {
      console.log('Error duplicating plant', e);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emit('fetch-plants');
    }
  }

  mounted() {
    this.handleNameChange = debounce(
      this.handleNameChange,
      NAME_VALIDATION_DEBOUNCE_TIME
    );
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  async handleNameChange() {
    // validate the cloned list
    this.plantsToCreate = [
      ...NamingService.validatePlantAgainstList(this.plantsToCreate),
    ];
  }

  // DO NOT DELETE ME
  // I'm used in the watcher
  private dispose() {
    NamingService.flushCache();
    this.entitiesPrefetchDone = false;
    this.plantsCount = 1;
    this.plantsToCreate = [];
  }

  // DO NOT DELETE ME
  // I'm used in the watcher
  private async doFirstPrefetch() {
    this.isLoading = true;
    await NamingService.prefetchFlatEntities(
      +this.plant.desktopId!,
      PlantEntityType.PLANT
    );
    this.entitiesPrefetchDone = true;
    this.isLoading = false;
  }

  // DO NOT DELETE ME
  // I'm used in the watcher
  private async initializeFirstName() {
    this.isLoading = true;
    await this.suggestNameIfNeeded(0, 0);
    this.isLoading = false;
    console.log(this.plantsToCreate);
    return;
  }

  // DO NOT DELETE ME
  // I'm used in the watcher
  private async suggestNameIfNeeded(prevValue: number, currValue: number) {
    if (currValue === 0) {
      this.plantsCount = 1;
      this.plantsToCreate = [
        {
          name: await NamingService.suggestNextValidEntityName({
            eType: PlantEntityType.PLANT,
            plantId: +this.plant.desktopId!,
            metadata: { plantSourceName: this.plant.name },
          }),
        },
      ];
      this.updateNamingServiceAdditionalEntities();
      return;
    }

    if (prevValue > currValue) {
      this.plantsToCreate.pop();
      this.updateNamingServiceAdditionalEntities();
      return;
    }

    this.plantsToCreate.push({
      name: await NamingService.suggestNextValidEntityName({
        eType: PlantEntityType.PLANT,
        plantId: +this.plant.desktopId!,
        metadata: { plantSourceName: this.plant.name },
      }),
    });

    this.updateNamingServiceAdditionalEntities();
  }

  private updateNamingServiceAdditionalEntities() {
    NamingService.additionalEntities = this.plantsToCreate.map(
      (p, id) =>
        new PlantFlatEntityDTO({
          id: id.toString(),
          currentName: p.name,
        })
    );
  }
}
